/**
 * `/reset-password/$token` — set a new password from a reset link (auth-and-authz.md
 * §1.5). Enforces the min-12 client-side (the server re-checks min-12 + HIBP). On
 * success, all sessions are revoked server-side, so the user is sent to /login — carrying a
 * history-state flag that the login screen turns into a one-line "password changed" note.
 * (History state, not a search param: `/login`'s `validateSearch` keeps only `redirect`, and
 * a completed reset is not a bookmarkable address anyway.)
 *
 * F-34 — the `autocomplete="username"` field. The route is TOKEN-ONLY (`/reset-password/$token`,
 * no email in the path or search), so on mount the screen asks worker-auth's
 * `GET /auth/password/reset/{token}` for the MASKED address the token is for (`email_hint`,
 * `a***@example.com`) and shows it as a read-only "Account" field above the new password, so a
 * password manager pairs the new credential with the right identity (the same reason
 * `/invite/$token` has one). The lookup is `retry: false` and never consumes the token; a 404
 * (unknown / consumed / expired — one shape, not a lifecycle oracle) simply omits the field, and
 * the existing invalid-token handling on SUBMIT is unchanged. A blank or invented username field
 * would be worse than none (a manager would save the credential against the wrong identity), so
 * the field only ever renders with a server-confirmed hint.
 */
import { useQuery } from '@tanstack/react-query';
import { useNavigate, type HistoryState } from '@tanstack/react-router';
import { useId, useState, type FormEvent, type JSX } from 'react';

import {
  AuthCard,
  FormError,
  PasswordField,
  ReadonlyIdentityField,
  SubmitButton,
} from '../components/ui.tsx';
import { api, getErrorMessage } from '../lib/api.ts';
import { getRequestId } from '../lib/request-id.ts';

const MIN_PASSWORD = 12;
/** Shared so the render can tell WHICH field the one error region is about (the confirmation). */
const MISMATCH_COPY = 'Passwords do not match.';

/** The history state a successful reset hands to `/login`. */
interface PasswordChangedState extends HistoryState {
  passwordChanged?: true;
}

/** The one-line note `/login` shows when it was reached from a successful reset. */
export const PASSWORD_CHANGED_NOTE =
  'Your password has been changed. Sign in with the new password.';

/** True when the current location's history state was set by a successful reset. */
export function wasPasswordChanged(state: HistoryState): boolean {
  return (state as PasswordChangedState).passwordChanged === true;
}

export function ResetPasswordScreen({ token }: { token: string }): JSX.Element {
  const navigate = useNavigate();
  const errorId = useId();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [errorRequestId, setErrorRequestId] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  // F-34: whose password this is (masked). 404 → no field; the submit flow decides validity.
  const hint = useQuery({
    queryKey: ['auth', 'password-reset-hint', token],
    queryFn: () => api.auth.resetTokenHint(token),
    retry: false,
    staleTime: Infinity,
  });
  const emailHint = hint.data?.email_hint;

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD;
  const mismatch = confirm.length > 0 && confirm !== password;
  const message =
    error ??
    (tooShort ? `Use at least ${MIN_PASSWORD} characters.` : mismatch ? MISMATCH_COPY : null);
  // One error region, two fields: a mismatch is about the CONFIRMATION the user just typed; the
  // length rule and every server refusal are about the new password. Describe the right one.
  const describesConfirm = message === MISMATCH_COPY;

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setErrorRequestId(undefined);
    if (password.length < MIN_PASSWORD) {
      setError(`Password must be at least ${MIN_PASSWORD} characters.`);
      return;
    }
    if (password !== confirm) {
      setError(MISMATCH_COPY);
      return;
    }
    setBusy(true);
    try {
      await api.auth.resetPassword({ token, new_password: password });
      const changed: PasswordChangedState = { passwordChanged: true };
      await navigate({ to: '/login', state: changed });
    } catch (err) {
      setError(getErrorMessage(err));
      setErrorRequestId(getRequestId(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthCard
      title="Set a new password"
      subtitle="Choose a strong password you don't use elsewhere"
    >
      <form
        onSubmit={handleSubmit}
        aria-label="Set a new password"
        style={{ display: 'grid', gap: 12 }}
      >
        {emailHint ? (
          <ReadonlyIdentityField label="Account" name="email" value={emailHint} />
        ) : null}
        {/* Two INDEPENDENT reveals: seeing the new password is not consent to expose the
            confirmation, and reading them one at a time is how a typo is actually caught. */}
        <PasswordField
          label="New password"
          name="new_password"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          placeholder={`At least ${MIN_PASSWORD} characters`}
          {...(message !== null && !describesConfirm ? { describedBy: errorId } : {})}
        />
        <PasswordField
          label="Confirm password"
          name="confirm_password"
          value={confirm}
          onChange={setConfirm}
          autoComplete="new-password"
          {...(describesConfirm ? { describedBy: errorId } : {})}
        />
        <FormError message={message} id={errorId} requestId={error ? errorRequestId : null} />
        <SubmitButton disabled={busy || password.length < MIN_PASSWORD || password !== confirm}>
          {busy ? 'Saving…' : 'Set password'}
        </SubmitButton>
      </form>
    </AuthCard>
  );
}
